import { LOCALE_KEY } from '@/constants';
import { toTransportLanguage } from './language';
import { BIZ_CODE, resolveBizAction, type BizAction } from '@/constants/bizCode';
import { translate } from '@/i18n/runtime';
import { currentBasename, stripBasename, withBasename } from '@/router/basename';
import { feedback } from '@/utils/feedback';
import {
  invalidateSession,
  markSessionExpired,
  sessionRevision,
} from './sessionLifecycle';
import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type GenericAbortSignal,
} from 'axios';

export interface RequestOptions extends AxiosRequestConfig {
  /**
   * 免鉴权接口（登录 / 忘记密码）。
   *
   * 会话改成 httpOnly Cookie 之后**前端已经无法选择"这次不带凭证"**——Cookie 由浏览器
   * 按同源规则自动附加。保留这个开关是给调用方标记语义（哪些接口本就匿名可访问），
   * 请求拦截器不再据它做任何事。
   */
  skipAuth?: boolean;
  /** 出错时不弹全局提示 */
  skipErrorHandler?: boolean;
  /** local 由认证入口自行处理；contract 用于透传存储响应的代理，仅按正式业务码判断。 */
  sessionFailure?: 'local' | 'contract';
  /** 请求发起时的会话版本，仅由统一出口写入。 */
  sessionRevision?: number;
  /**
   * 拿到原始响应时回调一次，**在 `Result<T>` 拆封之前**。
   *
   * 存在的唯一理由是**响应头**：下面那个响应拦截器把 `{code,msg,content}` 拆成
   * `content` 返回，拆完响应头就拿不到了。而有些接口的并发版本号只在响应头上
   * （`sys.notify` 的乐观锁用 `ETag: "v<n>"`，请求体里没有 version），
   * 页面读不到它就发不出 `If-Match`，保存必然被后端 400。
   *
   * 放在这里而不是在页面里另起一个 axios 实例：`Accept-Language`、
   * 401 跳登录、业务错误弹窗全在这条链上，绕开一次就要把这四件事各抄一遍。
   *
   * ⚠️ 只用来**读**，不要在这个回调里改 `response`——拆封逻辑紧接着就要用它。
   */
  onResponse?: (response: AxiosResponse) => void;
  /**
   * 合并**同一时刻在飞**的同一个读请求（`method + url + params + data` 全相同才算同一个）。
   *
   * 只给「结果与调用方无关、且没有副作用」的读接口开：称谓、语言统计这类
   * 首屏会被多处各取一份的数据。开了之后同 tick 内的多次调用共用一次网络请求，
   * 各自拿到的还是同一份结果。**写接口一律不要开**。
   *
   * 三条约束（`http.test.ts` 里逐条有用例）：
   * - 结束后立刻退出合并表，下一次调用照常重新请求——这不是缓存，不承担陈旧风险
   * - 失败处置按**首个发起方**的选项执行（`skipErrorHandler` 等），后来者不再重复弹提示
   * - 某个调用方用自己的 `signal` 取消，只拒绝它自己；共享请求继续跑完，其他调用方照常拿到数据
   */
  dedupe?: boolean;
}

/**
 * 网关（`/api/v1/*` → 网关 → iam 等微服务）的实例。后台的接口都从这里走。
 *
 * 成功码 200（2026-10-20 起统一，与 HTTP 成功对齐），
 * 语言头、401 跳登录、错误弹窗、`skipErrorHandler` 都挂在这一条链上。
 *
 * `/api/v1` 是前端这一侧的路径：开发环境由 vite 代理削掉前缀转给网关，部署环境由 nginx 转发。
 *
 * 原来这里还有第二个实例，baseURL 是单体那套 `/api`（`VITE_API_BASE_URL`）：IAM-only 之后
 * 单体业务接口一律在前端出口被拒（见下面 {@link request}），那个实例连同它的成功码一起删了。
 * `.env.*` 里的 `VITE_API_BASE_URL` 保留着给部署脚本用，前端已不再读它。
 */
const gatewayInstance = axios.create({
  baseURL: import.meta.env.VITE_GATEWAY_BASE_URL ?? '/api/v1',
  timeout: 30000,
  withCredentials: true,
});

/**
 * 前端语言键 → 后端 `AcceptHeaderLocaleResolver` 认得的语言标签。
 *
 * 只有 `en-US` 需要改写：后端支持列表里是 `en`。`zh-TW` 是 2026-08-19 之前的旧存储值，
 * i18n 启动时会迁移写回，这里同样兜一道——不兜的话首屏那几个请求会退回默认语言。
 */

/**
 * 请求语言。**服务端的业务文案是按这个头解析的**——组织会务整片页面的状态名、
 * 置灰原因、空态说明都由服务端给，不带这个头就会退回默认繁體，出现
 * 「界面英文、里面的句子繁體」这种半截切换。
 */
function acceptLanguage() {
  const saved = localStorage.getItem(LOCALE_KEY);
  // 没选过语言时用界面的默认值（`i18n/index.tsx` 的 DEFAULT_LOCALE，繁體），
  // 不落回浏览器自己的 Accept-Language——那会让界面繁體、服务端句子简体
  if (!saved) return 'zh-HK';
  return toTransportLanguage(saved);
}

const withAcceptLanguage = (config: any) => {
  const lang = acceptLanguage();
  if (lang) config.headers['Accept-Language'] = lang;
  return config;
};

/**
 * 后台入口声明：`/platform`、`/hq` 路径入口下带 `X-Muou-Admin-Entry: platform|hq`。
 * 单域名后台上平台与总会可能是同一家租户，网关凭这个头区分；按机构域名访问时不带。
 */
export const withAdminEntry = (config: any) => {
  const entry = currentBasename().replace(/^\//, '');
  if (entry) config.headers['X-Muou-Admin-Entry'] = entry;
  return config;
};

gatewayInstance.interceptors.request.use(withAcceptLanguage);
gatewayInstance.interceptors.request.use(withAdminEntry);

/**
 * 出错提示文案的唯一读取点，按优先级取：
 * 1. 服务端按请求语言拼好的那一句（{@link serverMessageOf}）
 * 2. 服务端没给文案时，取业务码表里的本地词条（action.messageKey）
 * 3. 本地词条：没收到响应（断网、超时）说网络，收到了但没有服务端文案（nginx 的 502 页等）说请求失败。
 *    **不用 axios 的 `error.message`**：那是 "Network Error" 这类英文技术原文，界面是什么语言都一样弹英文
 *
 * 例外：缺权限码（{@link BizAction.withMissingCodes}）只报码与固定文案，服务端那句更长的解释不回显（10-09 §1.5）。
 */
const bizMessageOf = (error: any, action: BizAction): string => {
  if (action.messageKey && action.withMissingCodes) {
    const codes = missingCodesOf(error);
    // 码本身是 ASCII，分隔符用 `, `，三种语言都不别扭
    return codes.length
      ? translate(action.messageKey, { codes: codes.join(', ') })
      : translate('http.permissionMissingNoCodes');
  }
  const serverMsg = serverMessageOf(error);
  if (serverMsg) return serverMsg;
  if (action.messageKey) return translate(action.messageKey);
  return translate(localFailureKey(error));
};

/**
 * 403 拒绝响应里的缺失权限码（`content.missingCodes`）；没有这一格时回空数组。
 *
 * 码由公共库回、名称与包归属由前端按码查词条——服务端拿不到 iam 的词条（10-09 §1.5）。
 */
export function missingCodesOf(error: unknown): string[] {
  const e = error as
    | { response?: { data?: { content?: unknown } } }
    | null
    | undefined;
  const content = e?.response?.data?.content;
  const codes = (content as { missingCodes?: unknown } | null | undefined)
    ?.missingCodes;
  return Array.isArray(codes)
    ? codes.filter((code): code is string => typeof code === 'string' && !!code)
    : [];
}

/**
 * 旧单体接口在前端出口被拒时，错误对象上的 `name`（见 {@link request}）。
 *
 * 单独一个记号是为了让 {@link localFailureKey} 说对话：拒掉一次调用不是网络故障，
 * 说「网络异常」会让人去查网络，而真正要改的是「这个接口还没迁到微服务」。
 */
export const MONOLITH_RETIRED_ERROR = 'MONOLITH_RETIRED_ERROR';

/**
 * 服务端没给文案时用哪条本地词条：没收到响应（断网、超时）说网络，收到了但没有服务端文案（nginx 的 502 页等）
 * 说请求失败；旧单体接口被出口拒掉的（见 {@link MONOLITH_RETIRED_ERROR}）同样是「请求失败」——它连请求都没发出去。
 * 组件里自己兜底的调用方用 `serverMessageOf(error) ?? t(localFailureKey(error))`——用 useIntl 的 t，切语言会跟着重渲染
 */
export function localFailureKey(error: unknown): string {
  const e = error as { name?: unknown; response?: unknown } | null | undefined;
  if (e?.name === MONOLITH_RETIRED_ERROR) return 'http.requestFailed';
  return e?.response ? 'http.requestFailed' : 'http.networkError';
}

/**
 * 服务端按语言拼好的那一句；服务端没给（断网、超时、nginx 回的 502 HTML、响应体里没有 msg）时回 undefined。
 *
 * 自己兜底文案的调用方用它，**不要直接读 `error.message`**：axios 的错误必带 message，
 * 没有服务端文案时那是 "Network Error"、"Request failed with status code 502" 这类英文原文，
 * 拿 `error.message ?? 本地词条` 兜底，本地词条永远轮不到。
 */
export function serverMessageOf(error: unknown): string | undefined {
  const e = error as
    | { name?: string; serverMsg?: unknown; response?: { data?: unknown } }
    | null
    | undefined;
  let msg: unknown;
  if (e?.name === 'BizError') {
    msg = e.serverMsg;
  } else {
    // 只认统一响应体（带 code 的对象）：HTML 错误页、纯文本都不是服务端文案
    const body = e?.response?.data;
    if (body && typeof body === 'object' && 'code' in body) {
      msg = (body as { msg?: unknown }).msg;
    }
  }
  return typeof msg === 'string' && msg ? msg : undefined;
}

function bizError(body: any, config: any) {
  const error: any = new Error(body?.msg || translate('http.requestFailed'));
  error.name = 'BizError';
  // 服务端原话单独留一份：message 在服务端没给 msg 时是上面那句本地占位，不能当服务端文案用
  error.serverMsg = body?.msg;
  error.code = body?.code;
  error.traceId = body?.traceId;
  error.config = config;
  return error;
}

/**
 * 拆封一条成功响应。统一响应体 `{ code, msg, content, time, traceId }` 与后端
 * `common/result/Result.java` 一一对应：**业务数据在 `content`**，不在 `data`；
 * 成功码由调用方给（网关这一条是 {@link BIZ_CODE}.IAM_SUCCESS）。
 *
 * `@JsonInclude(NON_NULL)` 会省掉空字段，所以 `msg` / `traceId` 可能整个不存在。
 * 后端出错时**同时**给真实 HTTP 状态码（400/401/404/500…），所以业务错误多数走 axios
 * 的 reject 分支；这里只兜住「HTTP 200 但 code 不是成功码」的那种。
 */
const unwrapResponse = (successCode: number) => (response: AxiosResponse) => {
  // 拆封前把原始响应交给调用方。只有需要读响应头的接口会传这个钩子，
  // 没传的走原路，行为一字不变（2026-08-26 合 dev 并入）。
  (response.config as RequestOptions).onResponse?.(response);

  const body = response.data;
  // 统一响应体 { code, msg, content, time, traceId }（服务端 Result record）：成功码以外视为业务错误
  if (body && typeof body === 'object' && 'code' in body) {
    if (body.code !== successCode) throw bizError(body, response.config);
    return body.content;
  }
  return body;
};

/** 失败侧：把服务端文案与链路号补进 axios 的错误对象，两个实例共用同一份 */
const rejectResponse = (error: any) => {
  // 真实后端：4xx/5xx 走这里，服务端的多语言文案在 response.data.msg，
  // 不改写的话弹出来的是 axios 的 "Request failed with status code 400"。
  // 就地补字段而不是另造一个 Error——`response` / `config` / 取消标记都还要留着。
  const body = error?.response?.data;
  if (body && typeof body === 'object' && 'code' in body) {
    if (body.msg) error.message = body.msg;
    error.traceId = body.traceId;
  }
  return Promise.reject(error);
};

gatewayInstance.interceptors.response.use(
  unwrapResponse(BIZ_CODE.IAM_SUCCESS),
  rejectResponse,
);

/**
 * 一次失败响应里的业务码，两条来路：成功响应里码不对时抛的 BizError（`error.code`），
 * 与 HTTP 非 2xx 时服务端统一响应体里的 `code`。自己分流业务码的调用方用它对号入座。
 */
export function bizCodeOf(error: any): number | undefined {
  return error?.name === 'BizError' ? error.code : error?.response?.data?.code;
}

/** 页面局部反馈遇到会话失效时让统一出口处理，避免跳转前再弹一次普通错误。 */
export function isSessionFailure(error: unknown): boolean {
  const e = error as { response?: { status?: number }; config?: RequestOptions } | null;
  const status = e?.config?.sessionFailure === 'contract' ? undefined : e?.response?.status;
  return resolveBizAction(bizCodeOf(error), status).redirect === '/login';
}

type ModuleGateDeniedListener = () => void;

const moduleGateDeniedListeners = new Set<ModuleGateDeniedListener>();

/**
 * 业务码 18403001（模块未开通）时的通知口，由 `store/moduleGate` 注册去补刷门控。
 *
 * 这里只发信号、不 import store：http → service → store 会绕回 http 形成循环依赖，
 * 而统一出口本来也不该知道「门控状态放在哪」。返回取消订阅函数，测试用得上。
 */
export function onModuleGateDenied(
  listener: ModuleGateDeniedListener,
): () => void {
  moduleGateDeniedListeners.add(listener);
  return () => {
    moduleGateDeniedListeners.delete(listener);
  };
}

const GATE_TOAST_WINDOW_MS = 2000;
let lastGateToast = { text: '', at: 0 };

/**
 * 同一句门控文案 2 秒内只弹一条。
 *
 * 一个页面会同时发好几个接口，每个都回 18403001，服务端文案一模一样；五条叠在一起
 * 就是这个提示最糟的样子。只对这一个码去重，其他错误的提示行为不变。
 */
function isRepeatedGateToast(text: string): boolean {
  const now = Date.now();
  if (text === lastGateToast.text && now - lastGateToast.at < GATE_TOAST_WINDOW_MS) {
    return true;
  }
  lastGateToast = { text, at: now };
  return false;
}

/**
 * 出错时的统一处置：取消静默；提示静默与业务请求的会话清理互相独立。
 * 两个出口（{@link gatewayRequest} 与那个一律拒绝的 {@link request}）共用这一处，
 * 免得「另一边忘了跳登录」这类差异悄悄长出来。
 *
 * 对外导出是给**自己分流业务码的调用方**用的：它们传了 `skipErrorHandler` 自己处理
 * 认得的那几个码，剩下的码必须回到这一套（弹文案、401 跳登录页），不然就成了静默失败。
 *
 * @param force 跳过分流调用方自己那一层的 `skipErrorHandler`，按「没人处理」走全局那一套。
 *  认证入口的 sessionFailure 声明始终有效，force 只接管错误提示。
 */
export function applyFailureAction(error: any, options: RequestOptions = {}, force = false): void {
  // 请求被主动取消（切页/重新查询）不是错误，静默即可
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
  const opts = { ...options, ...(error?.config ?? {}) };
  const httpStatus: number | undefined = opts.sessionFailure === 'contract'
    ? undefined : error?.response?.status;
  const code = bizCodeOf(error);
  // 门控落后于网关时补刷一次；失败处置不受 skipErrorHandler 影响（这是状态同步，不是提示）
  if (code === BIZ_CODE.MODULE_GATE_DENIED) {
    moduleGateDeniedListeners.forEach((listener) => listener());
  }
  const action = resolveBizAction(code, httpStatus);
  if (action.redirect === '/login' && !action.stay && opts.sessionFailure !== 'local') {
    if (!invalidateSession(opts.sessionRevision)) return;
    // 回跳使用站内路径，整页地址补回平台或总会入口；已在登录页时不再跳自身。
    const { search, hash } = window.location;
    const pathname = stripBasename(window.location.pathname);
    if (pathname === '/login') return;
    const params = new URLSearchParams({ redirect: pathname + search + (hash ?? '') });
    // 整页跳转会销毁 toast，失效原因交给登录页常驻展示；再记一笔让登录页补弹一次
    // 「登录已过期」（体验条目 总会后台-016/026）。
    if (action.loginReason) params.set('reason', action.loginReason);
    markSessionExpired();
    window.location.href = `${withBasename('/login')}?${params.toString()}`;
    return;
  }
  if (force || !opts.skipErrorHandler) {
    const text = bizMessageOf(error, action);
    if (code === BIZ_CODE.MODULE_GATE_DENIED && isRepeatedGateToast(text)) return;
    feedback.message.error(text);
  }
}

/** 一次失败的收尾：先按上面那套处置，再把原错误抛出去 */
function failAndRethrow(error: any, options: RequestOptions): never {
  applyFailureAction(error, options);
  throw error;
}

/**
 * 旧单体（`/api/v1/*` 那一族）的调用出口：**一个请求都不发，直接拒绝**。
 *
 * 🔴 IAM-only 之后浏览器里只有 iam 那一条会话，旧单体后端既不该再收到本前端的请求，
 * 也不该再有机会拿旧 Cookie 认人。所以在这一处（唯一的出口）拦掉：`src/services/` 下
 * 约 300 个业务 service 一行不用改，它们照常调 `request`，只是再也出不了网。
 *
 * 表现是**明确失败**，不是静默：接口还没迁到微服务的页面（工作台、官网配置、组织会务、
 * 财务…）点下去会照常弹错误提示、控制台有据可查——这正是要的，比拿旧会话悄悄打到一个
 * 已退役的后端上强。文案用词条 `http.requestFailed`（见 {@link localFailureKey}），
 * 不谎报网络故障；`skipErrorHandler` 仍然管着弹不弹全局提示，`catch` 到的还是这个错误对象。
 *
 * 拒掉的错误对象上没有 `config` / `response`：`applyFailureAction` 取状态码会拿到 undefined，
 * `resolveBizAction` 回到空处置，不跳登录页——旧接口调不通跟「登录过期」是两回事。
 *
 * 调用签名与 umi request 相同：request<T>(url, { method, data, params })。
 */
export function request<T = any>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const error = new Error(translate('http.monolithRetired', { url }));
  error.name = MONOLITH_RETIRED_ERROR;
  // 异步拒绝：与真请求一样在微任务里失败，调用方那段 try/catch 的时序不用重新想
  return Promise.reject(error).catch((e: unknown) => failAndRethrow(e, options));
}

/**
 * 在飞的读请求：key（方法 + 地址 + 参数 + 请求体 + 请求语言）→ 共享的那次 promise。
 *
 * 只登记 `dedupe: true` 的调用；请求一有结论就删掉，所以它是**合并**不是缓存——
 * 不存在陈旧数据，也不需要失效逻辑。
 *
 * 请求语言必须进 key：服务端按 `Accept-Language` 返回人话文本，切语言时新页面发出的
 * 同一条读请求若与切换前那条在飞请求合并，拿到的是旧语言的响应，而且之后不会再重发。
 */
const inflightReads = new Map<string, Promise<unknown>>();

function dedupeKeyOf(url: string, options: RequestOptions): string {
  const method = (options.method ?? 'GET').toUpperCase();
  const params = options.params === undefined ? '' : JSON.stringify(options.params);
  const data = options.data === undefined ? '' : JSON.stringify(options.data);
  return `${method} ${url} ${params} ${data} ${acceptLanguage()}`;
}

/**
 * 把共享请求与「这一次调用方自己的 signal」绑定。
 *
 * 关键点：共享请求本身**不带**任何调用方的 signal——一个页面卸载时取消，
 * 不该把另一个页面还在等的同一份数据一起掐掉。取消只拒绝发起取消的那一个。
 */
function raceWithOwnSignal<T>(
  shared: Promise<T>,
  signal?: GenericAbortSignal,
): Promise<T> {
  // 传进来的 signal 不一定真的是 AbortSignal（axios 的 GenericAbortSignal 两个方法可选）：
  // 听不到就只共担结果，不做「取消只影响自己」这一层
  const abortSignal: AbortSignal | undefined =
    signal &&
    typeof signal.addEventListener === 'function' &&
    typeof signal.removeEventListener === 'function'
      ? (signal as AbortSignal)
      : undefined;
  if (!abortSignal) return shared;
  if (abortSignal.aborted) {
    return Promise.reject(new axios.CanceledError(translate('http.cancelled')));
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new axios.CanceledError(translate('http.cancelled')));
    abortSignal.addEventListener('abort', onAbort, { once: true });
    const settle = () => abortSignal.removeEventListener('abort', onAbort);
    shared.then(
      (value) => { settle(); resolve(value); },
      (error) => { settle(); reject(error); },
    );
  });
}

/**
 * 网关那一路的调用签名与 {@link request} 相同，只是走 {@link gatewayInstance}：
 * 成功码 200、baseURL 取 `VITE_GATEWAY_BASE_URL`（默认 `/api/v1`）。
 * 后台的 iam 接口（开号、重置密码、会话）全走它。
 *
 * `dedupe: true` 时同 key 的在飞请求合并成一次，见 {@link RequestOptions.dedupe}。
 */
export function gatewayRequest<T = any>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  options = { ...options, sessionRevision: sessionRevision() };
  const { dedupe, signal, ...rest } = options;
  if (!dedupe) {
    return gatewayInstance
      .request({ url, ...options })
      .then((res) => res as unknown as T)
      .catch((error: any) => failAndRethrow(error, options));
  }

  const key = `${options.sessionRevision} ${dedupeKeyOf(url, options)}`;
  let shared = inflightReads.get(key) as Promise<T> | undefined;
  if (!shared) {
    const created = gatewayInstance
      .request({ url, ...rest })
      .then((res) => res as unknown as T)
      // 错误处置按首个发起方的选项走：后来者共用这一次处置，不再重复弹提示
      .catch((error: any) => failAndRethrow(error, rest));
    inflightReads.set(key, created);
    const clear = () => {
      if (inflightReads.get(key) === created) inflightReads.delete(key);
    };
    created.then(clear, clear);
    shared = created;
  }
  return raceWithOwnSignal(shared, signal);
}

/** Download a server-issued private artifact URL without forwarding admin cookies or bearer tokens. */
export async function fetchPrivateArtifact(url: string): Promise<Blob> {
  const target = new URL(url);
  if (!['http:', 'https:'].includes(target.protocol) || target.username || target.password)
    throw new Error('Invalid artifact URL');
  const response = await fetch(target, {credentials: 'omit', redirect: 'error'});
  if (!response.ok) throw new Error(`Artifact download failed (${response.status})`);
  return response.blob();
}
